import type { ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface EmptyViewProps {
  /** Optional icon rendered above the title. */
  icon?: ReactNode;
  /** Heading. */
  title: string;
  /** Optional one-line description. */
  description?: string;
  /** Optional action element (link or button). Rendered below the description. */
  action?: ReactNode;
  /** Optional className merged onto the root container. */
  className?: string;
}

/**
 * Lightweight in-content empty state, styled to match <StatusView>'s
 * typographic idiom (font-display light title, muted description, centered)
 * but sized for embedding inside a page rather than replacing it — no giant
 * code glyph, and a shorter minimum height.
 */
export function EmptyView({ icon, title, description, action, className }: EmptyViewProps) {
  return (
    <div
      className={cn(
        'flex min-h-[40vh] flex-col items-center justify-center px-6 text-center',
        className,
      )}
    >
      {icon && <div className="mb-4 opacity-40">{icon}</div>}
      <h2 className="font-display text-xl font-light leading-[1.1]">{title}</h2>
      {description && (
        <p className="mt-3 max-w-sm text-sm opacity-60">{description}</p>
      )}
      {action && <div className="mt-8">{action}</div>}
    </div>
  );
}
